/**
 * Single source of truth for product hostnames.
 *
 * Configure via build-time env (do not hardcode product domains in app code):
 * - REACT_APP_ROOT_DOMAIN   apex product domain
 * - REACT_APP_DEPLOY_ENV    prod | test | dev | staging
 * - REACT_APP_APP_DOMAIN    optional override for tenant ERP base
 * - REACT_APP_API_URL       optional absolute API origin override
 * - REACT_APP_PUBLIC_WEB_DOMAIN  optional brochure base override
 *
 * Derived (when overrides unset):
 * - app:  {env}.{root}  or  {root} when prod
 * - api:  https://api.{app}
 * - sites: sites.{app}
 */

export type DeployEnv = 'prod' | 'test' | 'dev' | 'staging';

const ENV_LABELS = new Set(['test', 'dev', 'staging']);

function readEnvDomain(key: string): string {
  return (process.env[key] || '').trim().toLowerCase().replace(/^\.+|\.+$/g, '');
}

function currentHostname(): string {
  if (typeof window === 'undefined') return '';
  return window.location.hostname.toLowerCase().replace(/\.$/, '');
}

function isLocalHostname(host: string): boolean {
  return !host || host === 'localhost' || host === '127.0.0.1' || host.endsWith('.localhost');
}

function normalizeDeployEnv(raw: string): DeployEnv | null {
  const v = raw.trim().toLowerCase();
  if (v === 'production' || v === 'prod') return 'prod';
  if (v === 'test' || v === 'dev' || v === 'staging') return v;
  return null;
}

/** Apex product domain from REACT_APP_ROOT_DOMAIN (or inferred). */
export function rootDomain(): string {
  const explicit = readEnvDomain('REACT_APP_ROOT_DOMAIN');
  if (explicit) return explicit;

  const app = readEnvDomain('REACT_APP_APP_DOMAIN');
  if (app && app !== 'localhost') {
    const parts = app.split('.').filter(Boolean);
    if (parts.length >= 3 && ENV_LABELS.has(parts[0])) {
      return parts.slice(1).join('.');
    }
    return app;
  }

  const host = currentHostname();
  if (isLocalHostname(host)) return '';

  let parts = host.split('.').filter(Boolean);
  if (parts[0] === 'www' || parts[0] === 'api' || parts[0] === 'sites') {
    parts = parts.slice(1);
  }
  if (parts.length < 2) return '';
  if (parts.length >= 3 && ENV_LABELS.has(parts[0])) {
    return parts.slice(1).join('.');
  }
  if (parts.length >= 4 && ENV_LABELS.has(parts[parts.length - 3])) {
    return parts.slice(-2).join('.');
  }
  return parts.slice(-2).join('.');
}

/** Deployment lane: prod | test | dev | staging. */
export function deployEnv(): DeployEnv {
  const fromEnv = normalizeDeployEnv(process.env.REACT_APP_DEPLOY_ENV || '');
  if (fromEnv) return fromEnv;

  const app = readEnvDomain('REACT_APP_APP_DOMAIN');
  if (app === 'localhost') return 'dev';
  if (app) {
    const head = app.split('.')[0];
    if (ENV_LABELS.has(head)) return head as DeployEnv;
    return 'prod';
  }

  const host = currentHostname();
  if (isLocalHostname(host)) return 'dev';

  const root = rootDomain();
  if (root && (host === root || host === `www.${root}`)) return 'prod';
  if (root && host.endsWith(`.${root}`)) {
    const prefix = host.slice(0, -(root.length + 1));
    const labels = prefix.split('.').filter(Boolean);
    const envLabel = labels[labels.length - 1];
    if (ENV_LABELS.has(envLabel)) return envLabel as DeployEnv;
  }
  return 'prod';
}

/** Tenant ERP base host: `{slug}.{appBaseDomain}`. */
export function appBaseDomain(): string {
  const fromEnv = readEnvDomain('REACT_APP_APP_DOMAIN');
  if (fromEnv) return fromEnv;

  const host = currentHostname();
  if (host.endsWith('.localhost')) return 'localhost';

  const root = rootDomain();
  if (!root) return 'localhost';
  const env = deployEnv();
  return env === 'prod' ? root : `${env}.${root}`;
}

/** Brochure / public-web base: `{slug}.{publicWebBaseDomain}`. */
export function publicWebBaseDomain(): string {
  const fromEnv = readEnvDomain('REACT_APP_PUBLIC_WEB_DOMAIN');
  if (fromEnv) return fromEnv;
  const app = appBaseDomain();
  if (app === 'localhost') return 'sites.localhost';
  return `sites.${app}`;
}

/**
 * Absolute API origin, or '' for same-origin / CRA proxy.
 * Default: https://api.{appBaseDomain}.
 */
export function apiBaseUrl(): string {
  const explicit = (process.env.REACT_APP_API_URL || '').trim().replace(/\/+$/, '');
  if (explicit) return explicit;

  if (process.env.NODE_ENV === 'development') return '';

  const app = appBaseDomain();
  if (!app || app === 'localhost') return '';
  return `https://api.${app}`;
}
